import { useState } from "react";
import { Text, ActivityIndicator } from "react-native-paper";
import { Platform, View } from "react-native";
import { Controller, useForm } from "react-hook-form";
import { gql, useQuery, useMutation } from "@apollo/client";

import { ScreenProps } from "../types";
import {
  Button,
  CheckboxItem,
  useErrorScroll,
  ActionsContainer,
  ScreenContainer,
  AvatarProfile,
  ScrollableContainer,
} from "../../components";
import { formatDate } from "../../utils/dateUtils";
import { CURRENT_MEMBER_FRAGMENT, CurrentMember } from "../fragments";
import UnableToConfirmModal from "./UnableToConfirmModal";
import SuccessModal from "./SuccessModal";
import ActivityReviewInfoPopOver from "./ActivityReviewInfoPopOver";
import { isEligible } from "../utils";
import { GraphQLError } from "graphql";
import { useSettings } from "../../hooks/useSettings";

const Divider = () => <View className="border-t-2 border-sky-lighter" />;

type FormData = { checkboxTos: boolean };
const defaultValues: FormData = { checkboxTos: false };

export const QUERY = gql`
  query activityReviewQuery($id: ID!) {
    currentMember {
      ...CurrentMemberFragment
      submission: deferredCompSubmissionToReview(id: $id) {
        id
        completionDate
        comments
        participationLevel {
          id
          requirements
          activity {
            id
            name
            description
          }
        }
        submitter {
          id
          name
          firstName
          lastName
          profilePhoto
        }
      }
    }
  }
  ${CURRENT_MEMBER_FRAGMENT}
`;

export const MUTATION = gql`
  mutation approveDeferredCompSubmission($id: ID!) {
    approveDeferredCompSubmission(id: $id) {
      message
      success
    }
  }
`;

interface Submission {
  id: string;
  completionDate: string;
  comments: string;
  participationLevel: {
    requirements: string;
    activity: {
      name: string;
      description: string;
    };
  };
  submitter: {
    name: string;
    firstName: string;
    lastName: string;
    profilePhoto: string;
  };
}
interface Query {
  currentMember: CurrentMember & { submission: Submission };
}

const DEFAULT_ERROR = "An unknown error occurred while submitting this form. Please try again.";

export default function ActivityReviewScreen({ navigation, route }: ScreenProps<"ActivityReview">) {
  const { id } = route.params;
  const { data, loading, error } = useQuery<Query>(QUERY, {
    context: {
      callerWillHandleError(error: GraphQLError[] = [], networkError: Error) {
        if (networkError) return false;
        return error.every((error) => error?.extensions?.code === "NOT_FOUND");
      },
    },
    variables: { id },
    errorPolicy: "all",
  });
  const [sendMutation] = useMutation(MUTATION);

  const [apiError, setApiError] = useState("");
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<FormData>({ defaultValues, mode: "onChange" });
  const { onSubmitError } = useErrorScroll({ defaultValues });
  const [unableToConfirmModalVisible, setUnableToConfirmModalVisible] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const { activityReviewOnboarding, setActivityReviewOnboarding } = useSettings();

  if (loading) return <ActivityIndicator className="flex-1 items-center justify-center bg-sky-white" />;

  function navigateBack() {
    navigation.canGoBack() ? navigation.goBack() : navigation.replace("DeferredComp");
  }

  if (!data || error) {
    return (
      <ScreenContainer navigationTitle="Activity Review Request" goBack={navigateBack}>
        <View className="p-6 m-6 bg-white rounded-xl">
          <Text className="text-regular-tight font-700 mb-3">Your review request is no longer valid.</Text>
          <Text className="text-regular-normal">The activity may have expired or been cancelled by the requestor.</Text>
          <Button className="self-start mt-6" mode="secondary" onPress={navigateBack}>
            Close
          </Button>
        </View>
      </ScreenContainer>
    );
  }

  const onEnroll =
    isEligible(data?.currentMember?.eligibilityStatus) && !data?.currentMember.enrolled
      ? () => {
          setSuccessModalVisible(false);
          navigation.replace("Enroll");
        }
      : undefined;

  const onCloseSuccessModal = () => {
    setSuccessModalVisible(false);
    navigateBack();
  };

  const onPressSubmit = async () => {
    try {
      const {
        data: {
          approveDeferredCompSubmission: { success, message },
        },
      } = await sendMutation({ variables: { id } });

      if (success) {
        setSuccessModalVisible(true);
      } else {
        setApiError(message || DEFAULT_ERROR);
      }
    } catch (e: any) {
      const message = e && e.message ? e.message : DEFAULT_ERROR;
      setApiError(message);
    }
  };

  const onPressUnableToConfirm = () => setUnableToConfirmModalVisible(true);

  const submission = data?.currentMember.submission || {};

  const { firstName, lastName } = submission.submitter;

  return (
    <ScreenContainer navigationTitle="Activity Review Request" testID="ActivityReviewScreen" goBack={() => navigateBack()}>
      {Platform.OS === "web" && !activityReviewOnboarding && (
        <ActivityReviewInfoPopOver {...{ onDontShowAgain: setActivityReviewOnboarding }} />
      )}
      <UnableToConfirmModal
        {...{
          visible: unableToConfirmModalVisible,
          onDismiss: () => setUnableToConfirmModalVisible(false),
          id,
          onSuccess: () => {
            setUnableToConfirmModalVisible(false);
            navigateBack();
          },
        }}
      />
      <SuccessModal
        {...{
          visible: successModalVisible,
          onEnroll,
          onClose: onCloseSuccessModal,
        }}
      />
      <ScrollableContainer>
        <View className="m-6">
          <Text className="text-regular-normal font-400 text-ink-darkest">
            {submission.submitter.name} requests your confirmation of their participation in a deferred compensation-eligible
            activity
          </Text>
        </View>

        <Divider />

        <View className="m-6">
          <Text className="text-small-tight font-700 pb-4 text-ink-darkest">Requesting Member</Text>
          <View className="flex-row items-center">
            <AvatarProfile
              {...{
                size: 40,
                profilePhoto: submission.submitter.profilePhoto,
                firstName,
                lastName,
                testID: "submitter-image",
              }}
            />
            <Text className="text-regular-tight text-ink-darker font-500 ml-3">{submission.submitter.name}</Text>
          </View>
        </View>

        <Divider />

        <View className="m-6">
          <Text className="text-small-tight font-700 pb-3 text-ink-darkest">Activity</Text>
          <Text className="text-regular-tight text-ink-darker font-500">{submission.participationLevel.activity.name}</Text>
          <Text className="text-regular-tight font-400 text-ink-dark mt-2">
            {submission.participationLevel.activity.description}
          </Text>
        </View>

        <Divider />

        <View className="m-6">
          <Text className="text-small-tight font-700 pb-3 text-ink-darkest">Participation Level</Text>

          <Text className="text-regular-tight font-400 text-ink-dark mt-2">{submission.participationLevel.requirements}</Text>
        </View>

        <Divider />

        <View className="m-6">
          <Text className="text-small-tight font-700 pb-3 text-ink-darkest">Activity Completion Date</Text>

          <Text className="text-regular-tight font-400 text-ink-dark mt-2">
            {formatDate(submission.completionDate, "MMMM Do YYYY")}
          </Text>
        </View>

        <Divider />

        <View className="m-6">
          <Text className="text-small-tight font-700 pb-3 text-ink-darkest">Summary of Activity</Text>

          <Text className="text-regular-tight font-400 text-ink-dark mt-2">{submission.comments}</Text>
        </View>

        <Divider />

        <View className="m-6 flex-1">
          <Controller
            name="checkboxTos"
            control={control}
            rules={{
              required: "Agreement is required",
            }}
            render={({ field: { ref, ...fieldProps }, fieldState: { error } }) => (
              <CheckboxItem
                {...fieldProps}
                label="I attest, based on my personal knowledge or based on my receipt of evidence sufficiently demonstrating completion, that the PGA Member referenced above has completed the noted action and activity.  Further, I acknowledge that I am aware that providing incomplete and/or inaccurate information in connection with this attestation may result in my conduct being reviewed under the PGA's Code of Ethics."
                labelStyle="text-regular-normal text-left ml-2 mr-6"
                className="items-start px-0 mx-0"
                testID="checkboxTos"
                error={error}
              />
            )}
          />
        </View>

        <ActionsContainer errorAlert errorMessage={apiError} onCloseErrorMessage={setApiError}>
          <Button
            loading={isSubmitting}
            disabled={isSubmitting}
            isValid={isValid}
            onPress={handleSubmit(onPressSubmit, onSubmitError)}
            mode="contained"
            icon="send"
            iconRight
          >
            Confirm Activity
          </Button>
          <Button onPress={onPressUnableToConfirm} mode="text">
            Unable to Confirm?
          </Button>
        </ActionsContainer>
      </ScrollableContainer>
    </ScreenContainer>
  );
}
